import { getUniqueScoredWordCount } from '~/boggle/config/runSummaryScore'
import { standardLexicon } from '~/boggle/engine/standard/lexicon'
import type { BoggleRunLedgerEntry } from '~/boggle/engine/standard/types'

import type { BoggleBadgeFamilyId } from '~/boggle/config/badges/familyTypes'
import {
  acceptedIndicesThrough,
  ledgerHasAnyCorrection,
} from './checkLedger'
import { maxInterWordGapMsForBattery } from './ledgerMetrics'
import { getBurstActiveAtAcceptance, getDuringMeterBurstWindow } from '~/boggle/lib/ledgerMeterBurstFacts'
import { isAcceptedWordEntry, isRunEndEntry } from './checkTypes'
import {
  countRareLetterOccurrences,
  isAlphabeticalOrderWord,
  maxSameGoldTileUsesInPath,
} from './wordAnalysis'
import { bufferedTimeMs } from './timeBuffer'

const QUICKDRAW_WINDOW_MS = bufferedTimeMs(3000)
const MACHINE_WINDOW_MS = bufferedTimeMs(20_000)
const MACHINE_MIN_LENGTH = 5

function countAcceptedInWindow(
  ledger: readonly BoggleRunLedgerEntry[],
  index: number,
  windowMs: number,
  minLength = 1,
): number {
  const entry = ledger[index]
  if (!isAcceptedWordEntry(entry)) {
    return 0
  }
  const accepted = acceptedIndicesThrough(ledger, index).filter(candidateIndex => {
    const candidate = ledger[candidateIndex]!
    return isAcceptedWordEntry(candidate) && candidate.word.length >= minLength
  })
  let inWindow = 0
  for (let position = accepted.length - 1; position >= 0; position -= 1) {
    const candidateIndex = accepted[position]!
    const candidate = ledger[candidateIndex]!
    if (!isAcceptedWordEntry(candidate)) {
      continue
    }
    if (entry.atMs - candidate.atMs > windowMs) {
      break
    }
    inWindow += 1
  }
  return inWindow
}

function correctionFreeAcceptStreak(
  ledger: readonly BoggleRunLedgerEntry[],
  index: number,
): number {
  let streak = 0
  for (let i = 0; i <= index; i++) {
    const sliceEntry = ledger[i]
    if (sliceEntry?.kind === 'correction') {
      streak = 0
    } else if (isAcceptedWordEntry(sliceEntry)) {
      streak += 1
    }
  }
  return streak
}

function uniqueWordAcceptStreak(
  ledger: readonly BoggleRunLedgerEntry[],
  index: number,
): number {
  const seenInStreak = new Set<string>()
  let streak = 0
  for (let i = 0; i <= index; i++) {
    const sliceEntry = ledger[i]
    if (!isAcceptedWordEntry(sliceEntry)) {
      continue
    }
    if (seenInStreak.has(sliceEntry.word)) {
      seenInStreak.clear()
      seenInStreak.add(sliceEntry.word)
      streak = 1
    } else {
      seenInStreak.add(sliceEntry.word)
      streak += 1
    }
  }
  return streak
}

function acceptedLengthsThrough(
  ledger: readonly BoggleRunLedgerEntry[],
  index: number,
): Set<number> {
  const lengths = new Set<number>()
  for (let i = 0; i <= index; i++) {
    const candidate = ledger[i]
    if (isAcceptedWordEntry(candidate)) {
      lengths.add(candidate.word.length)
    }
  }
  return lengths
}

function uniqueLettersThrough(
  ledger: readonly BoggleRunLedgerEntry[],
  index: number,
): Set<string> {
  const letters = new Set<string>()
  for (let i = 0; i <= index; i++) {
    const candidate = ledger[i]
    if (isAcceptedWordEntry(candidate)) {
      for (const character of candidate.word.toUpperCase()) {
        letters.add(character)
      }
    }
  }
  return letters
}

function uniqueStartersThrough(
  ledger: readonly BoggleRunLedgerEntry[],
  index: number,
): Set<string> {
  const starters = new Set<string>()
  for (let i = 0; i <= index; i++) {
    const candidate = ledger[i]
    if (isAcceptedWordEntry(candidate) && candidate.word.length > 0) {
      starters.add(candidate.word[0]!.toUpperCase())
    }
  }
  return starters
}

function longWordCountThrough(
  ledger: readonly BoggleRunLedgerEntry[],
  index: number,
  minLength: number,
): number {
  let count = 0
  for (let i = 0; i <= index; i++) {
    const candidate = ledger[i]
    if (isAcceptedWordEntry(candidate) && candidate.word.length >= minLength) {
      count += 1
    }
  }
  return count
}

function meterBurstAcceptCountThrough(
  ledger: readonly BoggleRunLedgerEntry[],
  index: number,
): number {
  let count = 0
  for (let i = 0; i <= index; i++) {
    const candidate = ledger[i]
    if (isAcceptedWordEntry(candidate) && getDuringMeterBurstWindow(candidate)) {
      count += 1
    }
  }
  return count
}

function meetsVersatileTier(lengths: Set<number>, maxLength: number): boolean {
  for (let length = 3; length <= maxLength; length += 1) {
    if (!lengths.has(length)) {
      return false
    }
  }
  return true
}

function meetsQuickdrawTier(
  ledger: readonly BoggleRunLedgerEntry[],
  index: number,
  wordCount: number,
): boolean {
  return countAcceptedInWindow(ledger, index, QUICKDRAW_WINDOW_MS) === wordCount
}

function meetsMachineTier(
  ledger: readonly BoggleRunLedgerEntry[],
  index: number,
  wordCount: number,
  windowMs: number,
): boolean {
  return (
    countAcceptedInWindow(ledger, index, windowMs, MACHINE_MIN_LENGTH) === wordCount
  )
}

function meetsProspectorTier(
  ledger: readonly BoggleRunLedgerEntry[],
  index: number,
  multiplier: number,
): boolean {
  const entry = ledger[index]
  if (!isAcceptedWordEntry(entry) || !(entry.meterCapacity > 0)) {
    return false
  }
  if (getDuringMeterBurstWindow(entry) || getBurstActiveAtAcceptance(entry)) {
    return false
  }
  const charged = entry.meterValueAfter + entry.meterOvermeterAfter
  return charged >= entry.meterCapacity * multiplier
}

function meetsAnadromeTier(
  ledger: readonly BoggleRunLedgerEntry[],
  index: number,
  minLength: number,
): boolean {
  const entry = ledger[index]
  if (!isAcceptedWordEntry(entry) || entry.word.length < minLength) {
    return false
  }
  const reversed = entry.word.split('').reverse().join('')
  if (reversed === entry.word) {
    return false
  }
  return standardLexicon.isDictionaryWord(reversed)
}

function meetsSteadyRunEndTier(ledger: readonly BoggleRunLedgerEntry[], index: number): boolean {
  const entry = ledger[index]
  if (!isRunEndEntry(entry) || getUniqueScoredWordCount(entry.acceptedWords) < 40) {
    return false
  }
  return !ledgerHasAnyCorrection(ledger)
}

function meetsLexicographerRunEndTier(
  ledger: readonly BoggleRunLedgerEntry[],
  index: number,
): boolean {
  const entry = ledger[index]
  if (!isRunEndEntry(entry) || getUniqueScoredWordCount(entry.acceptedWords) < 40) {
    return false
  }
  return new Set(entry.acceptedWords).size === entry.acceptedWords.length
}

const BATTERY_MIN_WORD_COUNT = 10

function meetsGreedyTier(
  ledger: readonly BoggleRunLedgerEntry[],
  index: number,
  minUses: number,
): boolean {
  const entry = ledger[index]
  if (!isAcceptedWordEntry(entry)) {
    return false
  }
  return maxSameGoldTileUsesInPath(entry.pathSteps) >= minUses
}

function meetsBatteryRunEndTier(
  ledger: readonly BoggleRunLedgerEntry[],
  index: number,
  maxGapMs: number,
): boolean {
  const entry = ledger[index]
  if (!isRunEndEntry(entry) || entry.acceptedWordCount < BATTERY_MIN_WORD_COUNT) {
    return false
  }
  return maxInterWordGapMsForBattery(ledger) <= maxGapMs
}

function meetsAbecedarianTier(
  ledger: readonly BoggleRunLedgerEntry[],
  index: number,
  minLength: number,
): boolean {
  const entry = ledger[index]
  if (!isAcceptedWordEntry(entry)) {
    return false
  }
  return isAlphabeticalOrderWord(entry.word, minLength, true)
}

function evaluateFamilyHighestTier(
  familyId: BoggleBadgeFamilyId,
  ledger: readonly BoggleRunLedgerEntry[],
  index: number,
): number {
  switch (familyId) {
    case 'quickdraw': {
      if (meetsQuickdrawTier(ledger, index, 5)) return 3
      if (meetsQuickdrawTier(ledger, index, 4)) return 2
      if (meetsQuickdrawTier(ledger, index, 3)) return 1
      return 0
    }
    case 'machine': {
      if (meetsMachineTier(ledger, index, 8, MACHINE_WINDOW_MS)) return 3
      if (meetsMachineTier(ledger, index, 7, MACHINE_WINDOW_MS)) return 2
      if (meetsMachineTier(ledger, index, 6, MACHINE_WINDOW_MS)) return 1
      return 0
    }
    case 'versatile': {
      const lengths = acceptedLengthsThrough(ledger, index)
      if (meetsVersatileTier(lengths, 9)) return 4
      if (meetsVersatileTier(lengths, 8)) return 3
      if (meetsVersatileTier(lengths, 7)) return 2
      if (meetsVersatileTier(lengths, 6)) return 1
      return 0
    }
    case 'prospector': {
      if (meetsProspectorTier(ledger, index, 2)) return 3
      if (meetsProspectorTier(ledger, index, 1.5)) return 2
      if (meetsProspectorTier(ledger, index, 1)) return 1
      return 0
    }
    case 'frenzy': {
      const count = meterBurstAcceptCountThrough(ledger, index)
      if (count >= 10) return 3
      if (count >= 6) return 2
      if (count >= 3) return 1
      return 0
    }
    case 'cartographer': {
      const entry = ledger[index]
      if (!isAcceptedWordEntry(entry)) {
        return 0
      }
      const uniqueTiles = entry.uniqueTilesCleared
      if (uniqueTiles >= 8) return 4
      if (uniqueTiles >= 7) return 3
      if (uniqueTiles >= 6) return 2
      if (uniqueTiles >= 5) return 1
      return 0
    }
    case 'pizazz': {
      const entry = ledger[index]
      if (!isAcceptedWordEntry(entry)) {
        return 0
      }
      const rareCount = countRareLetterOccurrences(entry.tileLetters)
      if (rareCount >= 3) return 2
      if (rareCount >= 2) return 1
      return 0
    }
    case 'anadrome': {
      if (meetsAnadromeTier(ledger, index, 6)) return 2
      if (meetsAnadromeTier(ledger, index, 5)) return 1
      return 0
    }
    case 'clutch': {
      const entry = ledger[index]
      if (!isAcceptedWordEntry(entry)) {
        return 0
      }
      const count = entry.overtimeSurvivingSelectionCount
      if (count >= 8) return 3
      if (count >= 5) return 2
      if (count >= 2) return 1
      return 0
    }
    case 'juggernaut': {
      if (longWordCountThrough(ledger, index, 8) >= 3) return 4
      if (longWordCountThrough(ledger, index, 7) >= 3) return 3
      if (longWordCountThrough(ledger, index, 6) >= 3) return 2
      if (longWordCountThrough(ledger, index, 5) >= 3) return 1
      return 0
    }
    case 'sesquipedalian': {
      const entry = ledger[index]
      if (!isAcceptedWordEntry(entry)) {
        return 0
      }
      if (entry.word.length >= 10) return 3
      if (entry.word.length >= 9) return 2
      if (entry.word.length >= 8) return 1
      return 0
    }
    case 'generalist': {
      const starters = uniqueStartersThrough(ledger, index)
      if (starters.size >= 19) return 3
      if (starters.size >= 17) return 2
      if (starters.size >= 15) return 1
      return 0
    }
    case 'steady': {
      if (meetsSteadyRunEndTier(ledger, index)) {
        return 4
      }
      const streak = correctionFreeAcceptStreak(ledger, index)
      if (streak >= 30 && isAcceptedWordEntry(ledger[index])) return 3
      if (streak >= 20 && isAcceptedWordEntry(ledger[index])) return 2
      if (streak >= 10 && isAcceptedWordEntry(ledger[index])) return 1
      return 0
    }
    case 'lexicographer': {
      if (meetsLexicographerRunEndTier(ledger, index)) {
        return 4
      }
      const uniqueStreak = uniqueWordAcceptStreak(ledger, index)
      if (uniqueStreak >= 30 && isAcceptedWordEntry(ledger[index])) return 3
      if (uniqueStreak >= 20 && isAcceptedWordEntry(ledger[index])) return 2
      if (uniqueStreak >= 10 && isAcceptedWordEntry(ledger[index])) return 1
      return 0
    }
    case 'greedy': {
      if (meetsGreedyTier(ledger, index, 4)) return 1
      return 0
    }
    case 'battery': {
      if (meetsBatteryRunEndTier(ledger, index, bufferedTimeMs(3000))) return 3
      if (meetsBatteryRunEndTier(ledger, index, bufferedTimeMs(5000))) return 2
      if (meetsBatteryRunEndTier(ledger, index, bufferedTimeMs(7000))) return 1
      return 0
    }
    case 'pangrammatist': {
      const letters = uniqueLettersThrough(ledger, index)
      if (letters.size >= 26) return 4
      if (letters.size >= 25) return 3
      if (letters.size >= 22) return 2
      if (letters.size >= 18) return 1
      return 0
    }
    case 'abecedarian': {
      if (meetsAbecedarianTier(ledger, index, 6)) return 3
      if (meetsAbecedarianTier(ledger, index, 5)) return 2
      if (meetsAbecedarianTier(ledger, index, 4)) return 1
      return 0
    }
    default:
      return 0
  }
}

export function getHighestTieredFamilyTierAtIndex(
  familyId: BoggleBadgeFamilyId,
  ledger: readonly BoggleRunLedgerEntry[],
  index: number,
): number {
  return evaluateFamilyHighestTier(familyId, ledger, index)
}

export const BOGGLE_TIERED_FAMILY_IDS: readonly BoggleBadgeFamilyId[] = [
  'quickdraw',
  'machine',
  'versatile',
  'prospector',
  'frenzy',
  'cartographer',
  'pizazz',
  'anadrome',
  'clutch',
  'juggernaut',
  'sesquipedalian',
  'generalist',
  'steady',
  'lexicographer',
  'greedy',
  'battery',
  'pangrammatist',
  'abecedarian',
] as const
